/**
 * Install detection: standalone mode and Android deferred install prompt.
 * No auto-trigger; prompt only via user interaction.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { isAndroid, isMobileBrowser } from './platformDetection';
import * as analytics from '../utils/analytics';

/**
 * @returns {boolean} True if the app is running in PWA standalone mode.
 */
export function isStandaloneMode() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
}

/**
 * Hook to capture and expose the Android install prompt. Only enabled when
 * mobile Android and not standalone. Prevents mini-infobar; prompt only on demand.
 * @param {{ enabled: boolean }} options - enabled when mobile Android and not standalone
 * @returns {{ canPrompt: boolean, promptInstall: () => Promise<{ triggered: boolean, outcome?: string }> }}
 */
export function useAndroidDeferredInstallPrompt({ enabled }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const deferredRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      deferredRef.current = e;
    };

    const handleAppInstalled = () => {
      analytics.event('pwa_install', {});
      setDeferredPrompt(null);
      deferredRef.current = null;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [enabled]);

  const promptInstall = useCallback(async () => {
    const prompt = deferredRef.current ?? deferredPrompt;
    if (!prompt) return { triggered: false };

    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      const outcome = choice?.outcome ?? 'dismissed';
      setDeferredPrompt(null);
      deferredRef.current = null;
      return { triggered: true, outcome };
    } catch {
      setDeferredPrompt(null);
      deferredRef.current = null;
      return { triggered: false };
    }
  }, [deferredPrompt]);

  const canPrompt = Boolean(deferredRef.current ?? deferredPrompt);

  return { canPrompt, promptInstall };
}

/**
 * Whether the Android deferred prompt hook should be enabled (mobile Android, not standalone).
 */
export function shouldEnableAndroidInstallHook() {
  return typeof window !== 'undefined' && isMobileBrowser() && isAndroid() && !isStandaloneMode();
}
